import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isToday from "dayjs/plugin/isToday";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(relativeTime);

import { DEFAULT_DAYS_FROM_END } from "@/common/constants/home";

export { _getDefaultStartEndDates as getDefaultStartEndDates };
export { _getTodayStartEndDate as getTodayStartEndDate };
export { _getFormattedDate as getFormattedDate };
export { _getFormattedDateNoYear as getFormattedDateNoYear };
export { _getFormattedTime as getFormattedTime };
export { _getFormatted as getFormatted };
export { _getDayOfWeek as getDayOfWeek };
export { _getTimeRange as getTimeRange };
export { _isToday as isToday };
export { _today as today };
export { _todayUtc as todayUtc };
export { _now as now };
export { _nowUtc as nowUtc };
export { _hoursAgo24 as hoursAgo24 };
export { _hoursAgo24Utc as hoursAgo24Utc };
export { _daysAgo7Utc as daysAgo7Utc };
export { _daysAgo30Utc as daysAgo30Utc };
export { _daysAgo as daysAgo };
export { _monthsAgo2 as monthsAgo2 };
export { _monthsAgo1 as monthsAgo1 };
export { _firstDay1MonthAgo as firstDay1MonthAgo };
export { _getNowRounded15Min as getNowRounded15Min };
export { _convertDatesToRange as convertDatesToRange };
export { _isDateStringEqual as isDateStringEqual };
export { _getRelativeTimeFromNow as getRelativeTimeFromNow };

const _getDefaultStartEndDates = () => {
  let END = _today + "T23:59:59.999Z";

  const START_DATE = dayjs(_today)
    .subtract(DEFAULT_DAYS_FROM_END, "days")
    .format("YYYY-MM-DD");

  const START = START_DATE + "T00:00:00.000Z";

  return {
    start: START,
    end: END,
  };
};

const _getTodayStartEndDate = (date) => {
  let start = dayjs.utc().format("YYYY-MM-DD") + "T00:00:00.000Z";
  let end = dayjs.utc().format("YYYY-MM-DD") + "T23:59:59.999Z";
  if (date) {
    start = dayjs(date).format("YYYY-MM-DD") + "T00:00:00.000Z";
    end = dayjs(date).format("YYYY-MM-DD") + "T23:59:59.999Z";
  }
  return {
    start: start,
    end: end,
  };
};

const _isToday = (date) => {
  return dayjs(date).isToday();
};

const _today = dayjs().format("YYYY-MM-DD");
const _todayUtc = dayjs().utc().format("YYYY-MM-DD");
const _now = dayjs().toISOString();
const _nowUtc = dayjs.utc().toISOString();
const _hoursAgo24 = dayjs().subtract(24, "hours").toISOString();

const _hoursAgo24Utc = dayjs(_nowUtc).subtract(24, "hours").toISOString();

const _daysAgo7Utc = dayjs(_nowUtc).subtract(7, "days").toISOString();
const _daysAgo30Utc = dayjs(_nowUtc).subtract(30, "days").toISOString();
const _daysAgo = (startString) => {
  const start = new Date(startString);
  const end = new Date();

  return Math.round(Math.abs(+start - +end) / 8.64e7);
};

const _monthsAgo2 = dayjs().subtract(2, "months").toISOString();
const _monthsAgo1 = dayjs().subtract(1, "months").toISOString();

const _firstDay1MonthAgo = () => {
  const now = dayjs();
  const firstOflastMonth = now
    .month(now.month() - 1)
    .date(1)
    .hour(0)
    .minute(0)
    .second(0);
  return firstOflastMonth.toISOString();
};

const _getTimeRange = (selectedFilter) => {
  let now;
  let hoursAgo24;
  let time;
  now = dayjs().toISOString();
  switch (selectedFilter.id) {
    case "past-day":
      hoursAgo24 = dayjs().subtract(24, "hours").toISOString();
      return { start: hoursAgo24, end: now };
    case "last-three-days":
      time = _getTodayStartEndDate(dayjs().subtract(3, "days").toISOString());
      return { start: time.start, end: now };
    case "last-seven-days":
      time = _getTodayStartEndDate(dayjs().subtract(7, "days").toISOString());
      return { start: time.start, end: now };
    case "last-fourteen-days":
      time = _getTodayStartEndDate(dayjs().subtract(14, "days").toISOString());
      return { start: time.start, end: now };
    case "last-twenty-one-days":
      time = _getTodayStartEndDate(dayjs().subtract(21, "days").toISOString());
      return { start: time.start, end: now };
    default:
      hoursAgo24 = dayjs().subtract(24, "hours").toISOString();
      return { start: hoursAgo24, end: now };
  }
};

const _getFormattedDate = (date) => {
  const OPTIONS = {
    dateOnly: true,
  };
  return _getFormatted(date, OPTIONS);
};

const _getFormattedDateNoYear = (date) => {
  const OPTIONS = {
    dateOnly: true,
    hideYear: true,
  };
  return _getFormatted(date, OPTIONS);
};

const _getFormattedTime = (date, options) => {
  options = typeof options !== "undefined" ? options : {};

  const OPTIONS = {
    timeOnly: true,
    hideSeconds: options.hideSeconds,
    hideUTC: options.hideUTC,
  };
  return _getFormatted(date, OPTIONS);
};

const _getFormatted = (date, options) => {
  let formatted = "";

  if (!date) {
    date = dayjs();
  }

  options = typeof options !== "undefined" ? options : {};
  let dateOnly =
    options.hasOwnProperty("dateOnly") && options.dateOnly
      ? options.dateOnly
      : false;
  let timeOnly =
    options.hasOwnProperty("timeOnly") && options.timeOnly
      ? options.timeOnly
      : false;
  let hideYear =
    options.hasOwnProperty("hideYear") && options.hideYear
      ? options.hideYear
      : false;
  let hideSeconds = options.hasOwnProperty("hideSeconds")
    ? options.hideSeconds
    : true;
  let hideUTC =
    options.hasOwnProperty("hideUTC") && options.hideUTC
      ? options.hideUTC
      : false;
  let showTimeFromNow =
    options.hasOwnProperty("showTimeFromNow") && options.showTimeFromNow
      ? options.showTimeFromNow
      : false;
  let showTimeFromNowLongTerm =
    options.hasOwnProperty("showTimeFromNowLongTerm") &&
    options.showTimeFromNowLongTerm
      ? options.showTimeFromNowLongTerm
      : false;

  let time = "";
  let seconds = "";
  if (!hideSeconds) {
    seconds = ":ss";
  }
  let utc = "";
  if (!hideUTC) {
    utc = "UTC";
  }
  time = "HH:mm" + seconds + " " + utc;

  if (dateOnly) {
    time = "";
  }

  if (timeOnly) {
    formatted = dayjs.utc(date).format(time);
  } else {
    let year = "";
    if (!hideYear) {
      year = ", YYYY";
    }
    formatted = dayjs.utc(date).format(`MMM D${year} ${time}`);
  }

  if (showTimeFromNow || showTimeFromNowLongTerm) {
    const IN_DATE = dayjs.utc(date);
    const NOW = dayjs().utc();
    if (NOW.diff(IN_DATE, "days") < 1 || showTimeFromNowLongTerm) {
      formatted = dayjs.utc(date).fromNow();
    }
  }

  return formatted.trim();
};

const _getDayOfWeek = (date) => {
  const DOW = dayjs(date).format("dddd");
  return DOW;
};

function _getNowRounded15Min() {
  //https://stackoverflow.com/a/21739514
  const timeToReturn = new Date();
  timeToReturn.setMilliseconds(
    Math.ceil(timeToReturn.getMilliseconds() / 1000) * 1000
  );
  timeToReturn.setSeconds(Math.ceil(timeToReturn.getSeconds() / 60) * 60);
  timeToReturn.setMinutes(Math.ceil(timeToReturn.getMinutes() / 15) * 15);
  return timeToReturn.toISOString();
}

const _convertDatesToRange = (startDate, endDate) => {
  let currentDate = new Date();
  let start = new Date(startDate);
  let end = new Date(endDate);
  let startDateResult =
    -1 * (Math.round(currentDate - start) / (1000 * 60 * 60 * 24)) + 1;
  let endDateResult =
    -1 * (Math.round(currentDate - end) / (1000 * 60 * 60 * 24));
  return [startDateResult.toFixed(0), endDateResult.toFixed(0)];
};

const _isDateStringEqual = (d1, d2) => {
  return new Date(d1).toDateString() === new Date(d2).toDateString();
};

const _getRelativeTimeFromNow = (d1) => {
  return dayjs(d1).fromNow();
};
